import React from 'react'
import { useFormContext, Controller } from 'react-hook-form'
import {
  Button,
  Card,
  CardContent,
  CardActions,
  createStyles,
  FormControl,
  Grid,
  makeStyles,
  TextField,
  Typography,
} from '@material-ui/core'

function formField({ section, field, control, errors }) {
  const fullFieldName = `${section.name}.${field.name}`
  let isError = false
  let errorMessage = ''
  if (errors && errors[`${section.name}`] && errors[`${section.name}`][`${field.name}`]) {
    isError = true
    errorMessage = `${errors[`${section.name}`][`${field.name}`].message}`
  }
  const fieldRequired = field.validations && field.validations.required ? true : false
  return (
    <Grid key={`${section.name}-${field.name}`} item xs={12}>
      <Controller
        as={TextField}
        control={control}
        label={`${field.label}${fieldRequired ? '' : ' (optional)'}`}
        name={fullFieldName}
        variant="outlined"
        fullWidth
        rules={field.validations}
        defaultValue={field.default}
        InputLabelProps={{
          className: fieldRequired ? 'required-label' : '',
          required: fieldRequired || false,
        }}
        multiline={field.multiLine ? true : false}
        rows={field.multiLine ? 3 : 1}
        error={isError}
        helperText={errorMessage || field.helperText}
      />
    </Grid>
  )
}

const useStyles = makeStyles(() =>
  createStyles({
    noBorder: {
      border: 'none',
      boxShadow: 'none',
    },
    cardGrid: {
      flexGrow: 1,
    },
    cardBackground: {
      background: '#d9edf7',
    },
  })
)

type Props = {
  formSections: Array,
  handleOnSubmit: () => void,
  handleErrors: () => void,
  handleCancel: () => void,
  handleBack: () => void,
}

function DetailsForm({
  formSections,
  handleOnSubmit,
  handleErrors,
  handleCancel,
  handleBack,
}: Props): Element<typeof Grid> {
  const classes = useStyles()
  const { control, errors, handleSubmit } = useFormContext()
  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Typography variant="h2">Invoice details</Typography>
        <Typography>
          These details are used for the purposes of creating this invoice and are not saved.
        </Typography>
      </Grid>
      <Grid item>
        <FormControl>
          <form id="private-invoice-form" onSubmit={handleSubmit(handleOnSubmit, handleErrors)}>
            <Grid container spacing={4}>
              {formSections.map((section) => {
                return (
                  <Grid item key={section.name} container spacing={3}>
                    <Grid item xs={12}>
                      <Typography variant="h4">{section.title}</Typography>
                      <Typography>{section.subTitle}</Typography>
                    </Grid>
                    {section.fields.map((field) => formField({ section, field, control, errors }))}
                  </Grid>
                )
              })}
            </Grid>
          </form>
        </FormControl>
      </Grid>
      <Grid classes={{ root: classes.cardGrid }} item>
        <Card
          classes={{ root: `${classes.cardBackground} ${classes.noBorder}` }}
          variant="outlined"
        >
          <Grid container>
            <Grid item>
              <CardContent>
                <Typography variant="h6">Create your invoice</Typography>
                <Typography variant="body1">
                  Your invoice will open in a new window for you to save and print. When you're
                  happy with the invoice, you can close this screen.
                </Typography>
              </CardContent>
            </Grid>
            <Grid item container justify="flex-end" xs={12}>
              <CardActions>
                <Button onClick={handleCancel}>Close</Button>
                <Button onClick={handleBack}>Back</Button>
                <Button
                  variant="contained"
                  color="primary"
                  type="submit"
                  form="private-invoice-form"
                >
                  Create Invoice
                </Button>
              </CardActions>
            </Grid>
          </Grid>
        </Card>
      </Grid>
    </Grid>
  )
}

export default DetailsForm
